/**
 * OfflineQueueContext
 * ────────────────────
 * Provides app-wide access to the offline mutation queue state.
 * Wrap your app (or Routes) with <OfflineQueueProvider> to enable
 * the global offline banner and queue helpers on every screen.
 *
 * Consumers:
 *   const { isOnline, pendingCount, syncing, enqueue, flush, syncStats, conflicts } = useOfflineQueue();
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  enqueueOfflineMutation,
  flushOfflineQueue,
  getPendingCount,
  getPendingCountByPriority,
  getLastSyncTime,
  getUnresolvedConflicts,
  resolveConflict,
  dismissConflict,
  subscribeToQueueChanges,
  PRIORITY,
} from '../utils/offlineQueue';
import { syncPendingMediaForTicket } from '../pages/ticket-management/components/OfflineMediaCapture';
import { supabase } from '../lib/supabase';

const OfflineQueueContext = createContext(null);

export function OfflineQueueProvider({ children }) {
  const [isOnline,     setIsOnline]     = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing,      setSyncing]      = useState(false);
  const [lastSyncAt,   setLastSyncAt]   = useState(null);
  const [syncStats,    setSyncStats]    = useState({ critical: 0, high: 0, low: 0, total: 0, failed: 0 });
  const [lastSyncResult, setLastSyncResult] = useState(null); // { synced, failed, conflicts }
  const [conflicts,    setConflicts]    = useState([]);
  const mountedRef = useRef(true);

  const refreshCount = useCallback(async () => {
    try {
      const [count, byPriority, lastSync, unresolvedConflicts] = await Promise.all([
        getPendingCount(),
        getPendingCountByPriority(),
        getLastSyncTime(),
        getUnresolvedConflicts(),
      ]);
      if (mountedRef?.current) {
        setPendingCount(count);
        setSyncStats(byPriority || { critical: 0, high: 0, low: 0, total: 0, failed: 0 });
        if (lastSync) setLastSyncAt(new Date(lastSync));
        setConflicts(unresolvedConflicts || []);
      }
    } catch {
      // IndexedDB unavailable (e.g. private browsing) — silently ignore
    }
  }, []);

  // Subscribe to queue changes (enqueue / flush)
  useEffect(() => {
    const unsub = subscribeToQueueChanges(refreshCount);
    refreshCount();
    return unsub;
  }, [refreshCount]);

  // Online / offline events
  useEffect(() => {
    const onOnline = () => {
      if (mountedRef?.current) setIsOnline(true);
    };
    const onOffline = () => {
      if (mountedRef?.current) setIsOnline(false);
    };
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Auto-flush when coming back online
  useEffect(() => {
    if (isOnline && pendingCount > 0) {
      handleFlush();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  const handleFlush = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const result = await flushOfflineQueue(async (mutation) => {
        // Handle media sync sentinel mutations
        if (mutation?.operation === 'media_sync' || mutation?.table === '__media_sync__') {
          const { ticketId } = mutation?.payload || {};
          if (ticketId) {
            try {
              await syncPendingMediaForTicket(ticketId, supabase);
            } catch (mediaErr) {
              console.warn('Offline media sync failed:', mediaErr?.message);
            }
          }
          return { data: null, error: null };
        }
      });
      if (mountedRef?.current) {
        setLastSyncAt(new Date());
        setLastSyncResult(result);
        await refreshCount();
      }
    } finally {
      if (mountedRef?.current) setSyncing(false);
    }
  }, [syncing, refreshCount]);

  const enqueue = useCallback(async (mutation) => {
    await enqueueOfflineMutation(mutation);
    await refreshCount();
  }, [refreshCount]);

  /**
   * Queue a check-in action while offline (CRITICAL priority).
   */
  const enqueueCheckin = useCallback(async ({ ticketId, technicianId, orgId, notes, checkinAt }) => {
    await enqueueOfflineMutation({
      table: 'ticket_checkins',
      operation: 'insert',
      priority: PRIORITY?.CRITICAL,
      payload: {
        ticket_id: ticketId,
        personnel_id: technicianId,
        organization_id: orgId,
        notes: notes || null,
        checkin_at: checkinAt || new Date()?.toISOString(),
      },
      label: `Check-in for ticket`,
    });
    await refreshCount();
  }, [refreshCount]);

  /**
   * Queue a check-out action while offline (CRITICAL priority).
   */
  const enqueueCheckout = useCallback(async ({ checkinId, checkoutAt, notes }) => {
    await enqueueOfflineMutation({
      table: 'ticket_checkins',
      operation: 'update',
      priority: PRIORITY?.CRITICAL,
      payload: {
        checkout_at: checkoutAt || new Date()?.toISOString(),
        notes: notes || null,
      },
      match: { id: checkinId },
      label: `Check-out`,
    });
    await refreshCount();
  }, [refreshCount]);

  /**
   * Queue an SMS send while offline (LOW priority — processed after critical ops).
   */
  const enqueueSms = useCallback(async ({ ticketId, ticketNumber, newStatus, technicianIds, orgId }) => {
    await enqueueOfflineMutation({
      table: '__sms_queue__',
      operation: 'sms_send',
      priority: PRIORITY?.LOW,
      payload: { ticketId, ticketNumber, newStatus, technicianIds, orgId },
      label: `SMS: ${newStatus} for ticket ${ticketNumber}`,
    });
    await refreshCount();
  }, [refreshCount]);

  /**
   * Resolve a detected conflict.
   * resolution: 'local' | 'server' | 'dismissed'
   */
  const handleResolveConflict = useCallback(async (conflictId, resolution, resolvedPayload) => {
    await resolveConflict(conflictId, resolution, resolvedPayload);
    await refreshCount();
  }, [refreshCount]);

  /**
   * Dismiss a conflict without applying changes.
   */
  const handleDismissConflict = useCallback(async (conflictId) => {
    await dismissConflict(conflictId);
    await refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  return (
    <OfflineQueueContext.Provider value={{
      isOnline,
      pendingCount,
      syncing,
      lastSyncAt,
      syncStats,
      lastSyncResult,
      conflicts,
      enqueue,
      flush: handleFlush,
      enqueueCheckin,
      enqueueCheckout,
      enqueueSms,
      resolveConflict: handleResolveConflict,
      dismissConflict: handleDismissConflict,
    }}>
      {children}
    </OfflineQueueContext.Provider>
  );
}

export function useOfflineQueue() {
  const ctx = useContext(OfflineQueueContext);
  if (!ctx) {
    // Graceful fallback when used outside provider
    return {
      isOnline:       navigator.onLine,
      pendingCount:   0,
      syncing:        false,
      lastSyncAt:     null,
      syncStats:      { critical: 0, high: 0, low: 0, total: 0, failed: 0 },
      lastSyncResult: null,
      conflicts:      [],
      enqueue:        async () => {},
      flush:          async () => {},
      enqueueCheckin: async () => {},
      enqueueCheckout: async () => {},
      enqueueSms:     async () => {},
      resolveConflict: async () => {},
      dismissConflict: async () => {},
    };
  }
  return ctx;
}

export default OfflineQueueContext;
